import { useCallback } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box,
} from '@mui/material'
import { ContentCopy, Share as ShareIcon } from '@mui/icons-material'
import { useToastStore } from '../../stores/toast.store'

interface ShareDialogProps {
  open: boolean
  onClose: () => void
  title: string
  content: string
}

export function ShareDialog({ open, onClose, title, content }: ShareDialogProps) {
  const toast = useToastStore()

  const handleWebShare = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: content })
      } catch {
        // User cancelled or share failed
      }
    }
  }, [title, content])

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(content)
      toast.success('Copied to clipboard')
    } catch {
      toast.error('Failed to copy to clipboard')
    }
  }, [content, toast])

  const canWebShare = typeof navigator !== 'undefined' && !!navigator.share

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Share - {title}</DialogTitle>
      <DialogContent>
        <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, mb: 2 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {content}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        {canWebShare && (
          <Button onClick={handleWebShare} startIcon={<ShareIcon />}>
            Share
          </Button>
        )}
        <Button onClick={handleCopy} startIcon={<ContentCopy />}>
          Copy to Clipboard
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
